import { Table, Tag, Button, Space } from '@/shared/ui/primitives';
import { DeleteOutlined } from '@/shared/ui/icons';
import { formatDateTime } from '@/shared/utils/formatters';
import { useTranslation } from 'react-i18next';
import type { ColumnsType, TableProps } from '@/shared/ui/subpath-types';

const TYPE_COLORS: Record<string, string> = {
  TEXT: 'default',
  IMAGE: 'blue',
  SYSTEM: 'gold',
};

interface Props {
  data: any[];
  loading: boolean;
  sortField: string;
  sortDir: string;
  onSort: (field: string) => void;
  onDelete: (item: any) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export default function MessageTable({ data, loading, sortField, sortDir, onSort, onDelete, page, pageSize, total, onPageChange }: Props) {
  const { t } = useTranslation();

  const columns: ColumnsType<any> = [
    { title: t('common.id'), dataIndex: 'id', sorter: true, width: 80 },
    {
      title: t('chat.chatRoomId'), dataIndex: 'chatRoomId', sorter: true, width: 110,
      render: (v: number) => `#${v}`,
    },
    {
      title: t('chat.sender'),
      render: (_: any, record: any) => record.senderName ? `${record.senderName} (#${record.senderId})` : '—',
    },
    {
      title: t('chat.content'), dataIndex: 'content', ellipsis: true, width: 300,
      render: (v: string) => v || '—',
    },
    {
      title: t('common.type'), dataIndex: 'type', sorter: true, width: 100,
      render: (v: string) => <Tag color={TYPE_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: t('filters.readStatus'), dataIndex: 'isRead', sorter: true, width: 90,
      render: (v: boolean) => <Tag color={v ? 'success' : 'error'}>{v ? t('common.yes') : t('common.no')}</Tag>,
    },
    {
      title: t('common.createdAt'), dataIndex: 'createdAt', sorter: true,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('common.actions'), width: 80,
      render: (_: any, record: any) => (
        <Space>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} />
        </Space>
      ),
    },
  ];

  const handleChange: TableProps['onChange'] = (_pagination, _filters, sorter: any) => {
    if (sorter?.field) onSort(sorter.field);
  };

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      onChange={handleChange}
      sortDirections={['ascend', 'descend']}
      showSorterTooltip={false}
      pagination={{
        current: page + 1,
        pageSize,
        total,
        showTotal: (total) => `${total} items`,
        showSizeChanger: false,
        onChange: (p) => onPageChange(p - 1),
      }}
    />
  );
}
